import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"
import { disableSubmitButtons } from "../form_submission"

export default class extends Controller {
  static targets = ["form", "status", "type", "parent", "position", "faq", "map", "errors"]

  connect() {
    this.dirty = this.element.dataset.pageEditorInvalid === "true"
    this.busy = false
    this.previousType = this.typeTarget.value
    this.beforeUnload = this.beforeUnload.bind(this)
    window.addEventListener("beforeunload", this.beforeUnload)
    this.showType()
    this.disableRemoved()
    if (this.hasErrorsTarget) this.errorsTarget.focus()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnload)
  }

  changed(event) {
    if (!this.formTarget.contains(event.target)) return
    this.dirty = true
    this.statusTarget.textContent = "Nesaglabātas izmaiņas"
  }

  typeChanged(event) {
    if (this.previousType === "faq" && this.typeTarget.value !== "faq" &&
        this.faqTarget.querySelector("fieldset:not(.pages-item-removed)") &&
        !window.confirm("Jautājumi vairs nebūs redzami publiskajā lapā, bet netiks dzēsti. Mainīt lapas veidu?")) {
      this.typeTarget.value = this.previousType
      event.stopPropagation()
      return
    }
    this.previousType = this.typeTarget.value
    this.showType()
  }

  showType() {
    // Hide presentation only: retain rich text and nested data when switching types.
    this.faqTarget.hidden = this.typeTarget.value !== "faq" && this.faqTarget.dataset.invalid !== "true"
    this.mapTarget.hidden = this.typeTarget.value !== "action_day"
    for (const input of this.faqTarget.querySelectorAll('input[name$="[question]"]')) {
      input.required = !this.faqTarget.hidden && !input.closest(".pages-item-removed")
    }
  }

  parentChanged() {
    const options = [["Sadaļas beigās", ""], ["Sadaļas sākumā", "1"]]
    const siblings = JSON.parse(this.element.dataset.pageEditorSiblings)
    siblings.filter(item => item.parent === this.parentTarget.value).forEach(item => {
      options.push([`Aiz: ${item.title}`, String(item.position)])
    })
    this.positionTarget.replaceChildren(...options.map(([label, value]) => new Option(label, value)))
  }

  removeItem(event) {
    if (!window.confirm("Noņemt šo ierakstu? Tas tiks dzēsts pēc lapas saglabāšanas.")) return
    const item = event.currentTarget.closest("fieldset")
    const destroy = item.querySelector('input[name$="[_destroy]"]')
    destroy.value = "1"
    item.classList.add("pages-item-removed")
    this.disableRemoved()
    this.changed(event)
  }

  disableRemoved() {
    for (const item of this.element.querySelectorAll(".pages-item-removed")) {
      for (const input of item.querySelectorAll("input, textarea, select")) input.required = false
    }
  }

  navigate(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.target === "_blank" || link.getAttribute("href").startsWith("#") ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    if (this.busy) {
      event.preventDefault()
      return
    }
    if (this.dirty && !window.confirm("Ir nesaglabātas izmaiņas. Atstāt lapu bez saglabāšanas?")) {
      event.preventDefault()
    } else {
      this.dirty = false
    }
  }

  async submit(event) {
    if (event.defaultPrevented || event.target !== this.formTarget) return
    event.preventDefault()
    event.stopPropagation()
    if (this.busy) return
    this.busy = true
    const restore = disableSubmitButtons(this.formTarget)
    this.formTarget.setAttribute("aria-busy", "true")
    this.formTarget.setAttribute("inert", "")
    this.statusTarget.textContent = "Saglabā…"
    try {
      await this.uploadFiles()
      const data = new FormData(this.formTarget)
      for (const input of this.formTarget.querySelectorAll('input[type="file"]')) {
        const uploaded = data.getAll(input.name).filter(value => typeof value === "string" && value.length)
        data.delete(input.name)
        uploaded.forEach(value => data.append(input.name, value))
      }
      const token = document.querySelector('meta[name="csrf-token"]')
      const headers = token ? { "X-CSRF-Token": token.content } : {}
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: data,
        credentials: "same-origin",
        headers
      })
      if (response.status === 422 || response.ok) {
        const documentFragment = new DOMParser().parseFromString(await response.text(), "text/html")
        const editor = documentFragment.querySelector(".pages-management")
        if (!editor) throw new Error("Missing editor")
        const alerts = document.querySelector(".alerts")
        const incomingAlerts = documentFragment.querySelector(".alerts")
        if (alerts && incomingAlerts) alerts.replaceWith(incomingAlerts)
        if (response.ok) {
          this.dirty = false
          window.history.replaceState(null, "", response.url)
          document.title = documentFragment.title
        }
        this.element.closest(".pages-management").replaceWith(editor)
      } else {
        throw new Error("Save failed")
      }
    } catch (_error) {
      this.dirty = true
      this.statusTarget.setAttribute("role", "alert")
      this.statusTarget.textContent = "Izmaiņas nav saglabātas. Pārbaudi savienojumu un mēģini vēlreiz."
    } finally {
      this.busy = false
      this.formTarget.removeAttribute("aria-busy")
      this.formTarget.removeAttribute("inert")
      restore()
    }
  }

  async uploadFiles() {
    for (const input of this.formTarget.querySelectorAll('input[type="file"][data-direct-upload-url]')) {
      if (!input.files.length) continue
      this.statusTarget.textContent = "Augšupielādē attēlu…"
      const file = input.files[0]
      const blob = await new Promise((resolve, reject) => {
        new DirectUpload(file, input.dataset.directUploadUrl).create((error, result) => {
          if (error) reject(error)
          else resolve(result)
        })
      })
      const hidden = document.createElement("input")
      for (const previous of this.formTarget.querySelectorAll('input[type="hidden"]')) {
        if (previous.name === input.name) previous.remove()
      }
      hidden.type = "hidden"
      hidden.name = input.name
      hidden.value = blob.signed_id
      input.before(hidden)
      input.value = ""
    }
    this.statusTarget.textContent = "Saglabā…"
  }

  uploadFailed() {
    this.dirty = true
    this.statusTarget.textContent = "Faila augšupielāde neizdevās. Izmaiņas nav saglabātas."
  }

  beforeUnload(event) {
    if (!this.dirty && !this.busy) return
    event.preventDefault()
    event.returnValue = ""
  }
}
